import { useAtom } from "@dbeining/react-atom";
import { useEffect, useState } from "react";
import { Nav, Navbar } from "react-bootstrap";
import { Scrollbars } from "react-custom-scrollbars-2";
import { NavLink, useLocation } from "react-router-dom";
import { userAtom } from "../../atoms";
import { isSidebarNavItemActive, sidebarNavItems } from "../../navigation/appNavigation";
import { filterNavItemsByRoles, getUserRoleNames } from "../../navigation/navVisibility";

type Props = {
    setShowMenu: React.Dispatch<React.SetStateAction<boolean>>;
    onCollapsedChange?: (isCollapsed: boolean) => void;
    onTransitionStart?: () => void;
    onTransitionEnd?: () => void;
    isCollapsed?: boolean;
};

export const NavigationBar: React.FC<Props> = ({ setShowMenu, onCollapsedChange, onTransitionStart, onTransitionEnd, isCollapsed = false }) => {
    const currentUser = useAtom(userAtom);
    const location = useLocation();
    const [internalCollapsed, setInternalCollapsed] = useState(false);

    const collapsedState = isCollapsed !== undefined ? isCollapsed : internalCollapsed;

    const userRoleNames = getUserRoleNames(currentUser?.identity.roles ?? []);

    const navigationItems = filterNavItemsByRoles(sidebarNavItems, userRoleNames);

    useEffect(() => {
        onCollapsedChange?.(collapsedState);
    }, [collapsedState, onCollapsedChange]);

    const handleToggleClick = () => {
        onTransitionStart?.();
        if (isCollapsed !== undefined) {
            onCollapsedChange?.(!collapsedState);
        } else {
            setInternalCollapsed(!collapsedState);
        }
    };

    const handleTransitionEnd = () => {
        onTransitionEnd?.();
    };

    return (
        <Navbar
            expand={false}
            expanded={true}
            sticky="top"
            id="side-nav"
            className={collapsedState ? "collapsed" : ""}
            onTransitionEnd={handleTransitionEnd}
        >
            <Navbar.Brand className="p-0" as={NavLink} to="/">
                <img
                    alt="Froom Waste Tracker"
                    src="/images/froom-waste-tracker-logo.svg"
                    className="d-inline-block align-top froom-header-logo side-nav-logo"
                />
            </Navbar.Brand>
            <Navbar.Collapse>
                <Scrollbars autoHide id="navbar-nav-scroll">
                    <Nav className="px-3 pb-5 mt-5">
                        {currentUser && (
                            <div>
                                {navigationItems.map((item) => {
                                    const isActive = isSidebarNavItemActive(item.path, location.pathname);
                                    return (
                                        <Nav.Link
                                            key={item.path}
                                            as={NavLink}
                                            to={item.path}
                                            className={isActive ? "nav-link active" : "nav-link"}
                                            onClick={() => setShowMenu(false)}
                                            title={collapsedState ? item.name : ""}
                                        >
                                            <div className="side-nav-logo-collapsed-container">
                                                <img
                                                    className="side-nav-logo-collapsed"
                                                    src={item.icon}
                                                    alt={item.name}
                                                    style={{
                                                        marginRight: collapsedState ? "0" : "0.75rem",
                                                        opacity: isActive ? 0 : 1
                                                    }}
                                                />
                                                <img
                                                    className="side-nav-logo-collapsed-selected"
                                                    src={item.selectedIcon}
                                                    alt={item.name}
                                                    style={{
                                                        marginRight: collapsedState ? "0" : "0.75rem",
                                                        opacity: isActive ? 1 : 0,
                                                    }}
                                                />
                                            </div>
                                            {!collapsedState && item.name}
                                        </Nav.Link>
                                    );
                                })}
                            </div>
                        )}
                    </Nav>
                </Scrollbars>

                <div className="sidebar-toggle-container">
                    <button
                        className="sidebar-toggle-btn"
                        onClick={handleToggleClick}
                        onTransitionEnd={handleTransitionEnd}
                        title={collapsedState ? "Expand Sidebar" : "Collapse Sidebar"}
                    >
                        {collapsedState ? "→" : "←"}
                    </button>
                </div>
            </Navbar.Collapse>
        </Navbar>
    );
};
